import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Circle } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { cn } from '../../../../helpers/shadCnUtils';
import { useUpdateUserPassword } from '@/api/mutations/useUpdateUserPassword';
import { ChangePasswordForm, changePasswordSchema } from '@/api/schemas/user';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';

const ChangePassword = ({ onSuccess }: { onSuccess: () => void }) => {
  const { mutate: updateUserPassword, isLoading } = useUpdateUserPassword();

  const { register, handleSubmit, watch } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const { newPassword, confirmPassword } = watch();

  const isPasswordValid = newPassword.length > 7;
  const isPasswordMatch = newPassword === confirmPassword && newPassword.length > 0;
  const isDisabled = !isPasswordValid || !isPasswordMatch;

  const onSubmit = (data: ChangePasswordForm) => {
    updateUserPassword(
      { newPassword: data.newPassword },
      {
        onSuccess,
      },
    );
  };

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4 flex flex-col gap-2">
          <Label htmlFor="new-password" className="text-sm font-medium text-gray-700">
            New Password
          </Label>
          <Input
            id="new-password"
            type="password"
            value={newPassword}
            placeholder="Enter your new password"
            className="w-full"
            autoComplete="off"
            autoCorrect="off"
            {...register('newPassword')}
          />
        </div>
        <div className="mb-4 flex flex-col gap-2">
          <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
            Confirm Password
          </Label>
          <Input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            placeholder="Confirm your new password"
            className="w-full"
            autoComplete="off"
            autoCorrect="off"
            {...register('confirmPassword')}
          />
        </div>
        <div>
          <p className="flex items-center gap-2 text-xs">
            <Circle
              size={12}
              fill={isPasswordValid ? 'var(--color-brandSecondary)' : ''}
              className={cn(
                'transition-colors duration-300',
                isPasswordValid ? 'text-brandSecondary' : 'text-gray-500',
              )}
            />
            Password must be at least 8 characters long
          </p>
          <p className="flex items-center gap-2 text-xs">
            <Circle
              size={12}
              fill={isPasswordMatch ? 'var(--color-brandSecondary)' : ''}
              className={cn(
                'transition-colors duration-300',
                isPasswordMatch ? 'text-brandSecondary' : 'text-gray-500',
              )}
            />
            Passwords must match
          </p>
        </div>
        <div className="flex justify-end">
          <Button variant="primary" disabled={isDisabled || isLoading} type="submit">
            Change Password
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
